import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChartLine, Lightning, Shield, ArrowRight, CheckCircle, Percent, Ticket } from "@phosphor-icons/react";

export default function Landing() {
  const [tools, setTools] = useState([]);
  useEffect(() => { api.get("/catalog").then((r) => setTools(r.data.tools || [])).catch(() => {}); }, []);

  return (
    <div data-testid="landing-page" className="min-h-screen">
      <section className="relative overflow-hidden hero-grid">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl -z-0" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24 md:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-primary/30 bg-primary/5 rounded-sm">
                <span className="w-1.5 h-1.5 bg-primary animate-pulse rounded-full" />
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary">Per-tool subscription · Bundle up to 40% off</span>
              </div>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]" data-testid="hero-title">
                Pro-grade<br/>
                <span className="text-primary">stock tools</span><br/>
                à la carte.
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                Subscribe to individual HTML tools month-to-month. The more you bundle, the bigger the discount — up to <span className="text-primary font-bold">40% off</span> when you take 10 or more.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/store"><Button data-testid="hero-cta-primary" size="lg" className="bg-primary text-black hover:bg-primary/90 font-bold rounded-sm px-8">Browse tools <ArrowRight size={18} weight="bold" className="ml-2" /></Button></Link>
                <Link to="/register"><Button data-testid="hero-cta-secondary" size="lg" variant="outline" className="border-border hover:border-primary/50 rounded-sm px-8">Free signup</Button></Link>
              </div>
              <div className="flex flex-wrap gap-6 pt-4 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-primary" /> Cancel anytime</div>
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-primary" /> Volume discounts auto-applied</div>
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-primary" /> Promo codes supported</div>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/20 blur-3xl -z-10" />
                <div className="border border-border bg-card rounded-sm overflow-hidden">
                  <div className="bg-black/50 border-b border-border px-4 py-2 flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-destructive/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-primary/70" />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground ml-2">terminal · bundle_calculator</span>
                  </div>
                  <div className="p-6 space-y-2 font-mono text-sm">
                    <div className="text-primary">$ stocktools discount --tools</div>
                    <div className="grid grid-cols-2 gap-y-1 py-3 border-y border-border">
                      <div className="text-muted-foreground">2 tools</div><div className="text-primary text-right">5% off</div>
                      <div className="text-muted-foreground">4 tools</div><div className="text-primary text-right">15% off</div>
                      <div className="text-muted-foreground">6 tools</div><div className="text-primary text-right">25% off</div>
                      <div className="text-muted-foreground">10+ tools</div><div className="text-yellow-400 text-right font-bold">40% off</div>
                    </div>
                    <div className="text-primary pt-1">✓ Applies automatically at checkout</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24 border-t border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: ChartLine, title: "Per-tool billing", desc: "Every tool has its own monthly price. Subscribe only to the tools you actually use." },
            { icon: Percent, title: "Volume discounts", desc: "Bundle 2+ tools and save automatically. Up to 40% off when you bundle 10 or more." },
            { icon: Ticket, title: "Promo codes", desc: "Enter a promo code at checkout for extra savings. Codes work on top of your bundle discount." },
          ].map((f, i) => (
            <div key={i} data-testid={`feature-card-${i}`} className="border border-border p-8 hover:border-primary/50 transition-colors rounded-sm">
              <f.icon size={32} weight="duotone" className="text-primary mb-4" />
              <h3 className="font-display font-extrabold text-2xl mb-3">{f.title}</h3>
              <p className="text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24 border-t border-border">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-primary mb-3">// The Arsenal</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Available tools</h2>
          </div>
          <Link to="/store" className="text-sm font-mono uppercase tracking-widest text-primary hover:underline">Browse all →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tools.slice(0, 6).map((t) => (
            <Link key={t.id} to="/store" data-testid={`landing-tool-${t.slug}`} className="border border-border p-6 hover:border-primary/40 hover:-translate-y-1 transition-all rounded-sm bg-card block">
              <div className="text-xs font-mono uppercase tracking-widest text-primary mb-3">{t.category}</div>
              <h3 className="font-display font-bold text-xl mb-2">{t.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">{t.description}</p>
              <div className="flex items-baseline gap-1"><span className="font-display font-black text-2xl">₹{t.price_inr}</span><span className="text-xs text-muted-foreground font-mono">/mo</span></div>
            </Link>
          ))}
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs font-mono text-muted-foreground uppercase tracking-widest">
          <div>© 2026 StockTools Pro</div>
          <div>Built for professional traders</div>
        </div>
      </footer>
    </div>
  );
}
